import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, Subject, map, tap } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import {
  ChatMessage, ChatMessagesPage, ChatReadEvent, ChatRelation, ChatRelationEvent, ChatRelations,
  ChatSearchResult, ChatTypingEvent, ChatUnread,
} from '../models/chat';
import { CallSocketEvent } from '../models/call';
import { AuthService } from './auth.service';

const EMPTY_UNREAD: ChatUnread = { messages: 0, requests: 0 };
const CALL_ACK_TIMEOUT_MS = 8000;

// Amis et messagerie entre membres (API /chat, protégée par JWT) + temps réel via socket.io.
// La connexion suit la session : ouverte à la connexion, fermée à la déconnexion (navigateur uniquement).
@Injectable({
  providedIn: 'root'
})
export class ChatService {

  private host = environment.host + '/chat';
  private socket: Socket | null = null;

  private relations = new BehaviorSubject<ChatRelations | null>(null);
  relations$ = this.relations.asObservable();

  private unread = new BehaviorSubject<ChatUnread>(EMPTY_UNREAD);
  unread$ = this.unread.asObservable();
  unreadTotal$: Observable<number> = this.unread$.pipe(map(unread => unread.messages + unread.requests));

  readonly messages$ = new Subject<ChatMessage>();
  readonly read$ = new Subject<ChatReadEvent>();
  readonly typing$ = new Subject<ChatTypingEvent>();
  readonly relationEvents$ = new Subject<ChatRelationEvent>();
  readonly callEvents$ = new Subject<CallSocketEvent>();

  // Ami dont la conversation est affichée : ses messages reçus ne comptent pas comme non lus
  activeConversation: string | null = null;

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    if (!isPlatformBrowser(platformId)) {
      return;
    }
    this.authService.currentUser$.pipe(
      map(user => user?.access_token || null),
      distinctUntilChanged(),
    ).subscribe(token => token ? this.connect(token) : this.disconnect());
    document.addEventListener('visibilitychange', () => this.onVisibilityChange());
  }

  get relationsValue(): ChatRelations | null {
    return this.relations.value;
  }

  get myId(): string | null {
    return this.authService.currentUserValue?._id ?? null;
  }

  relationOf(userId: string): ChatRelation {
    const relations = this.relations.value;
    if (!relations) {
      return 'none';
    }
    const same = (id: string) => String(id) === String(userId);
    if (relations.friends.some(friend => same(friend.user._id))) {
      return 'friend';
    }
    if (relations.incoming.some(request => same(request.user._id))) {
      return 'incoming';
    }
    if (relations.outgoing.some(request => same(request.user._id))) {
      return 'outgoing';
    }
    return 'none';
  }

  incomingRequestFrom(userId: string): string | null {
    return this.relations.value?.incoming.find(request => String(request.user._id) === String(userId))?._id ?? null;
  }

  loadRelations(): Observable<ChatRelations> {
    return this.storeRelations(this.httpClient.get<ChatRelations>(this.host + '/relations'));
  }

  loadUnread(): Observable<ChatUnread> {
    return this.httpClient.get<ChatUnread>(this.host + '/unread').pipe(tap(unread => this.unread.next(unread)));
  }

  searchUsers(query: string): Observable<ChatSearchResult[]> {
    return this.httpClient.get<ChatSearchResult[]>(this.host + '/users/search', { params: { q: query } });
  }

  sendRequest(userId: string): Observable<ChatRelations> {
    return this.storeRelations(this.httpClient.post<ChatRelations>(this.host + '/requests/' + userId, {}));
  }

  acceptRequest(requestId: string): Observable<ChatRelations> {
    return this.storeRelations(this.httpClient.post<ChatRelations>(this.host + '/requests/' + requestId + '/accept', {}));
  }

  // Refuser une demande reçue ou annuler une demande envoyée
  deleteRequest(requestId: string): Observable<ChatRelations> {
    return this.storeRelations(this.httpClient.delete<ChatRelations>(this.host + '/requests/' + requestId));
  }

  removeFriend(userId: string): Observable<ChatRelations> {
    return this.storeRelations(this.httpClient.delete<ChatRelations>(this.host + '/friends/' + userId));
  }

  getMessages(userId: string, before?: string): Observable<ChatMessagesPage> {
    const params: { [key: string]: string } = before ? { before } : {};
    return this.httpClient.get<ChatMessagesPage>(this.host + '/conversations/' + userId + '/messages', { params });
  }

  sendMessage(userId: string, text: string): Observable<ChatMessage> {
    return this.httpClient.post<ChatMessage>(this.host + '/conversations/' + userId + '/messages', { text });
  }

  markRead(userId: string): void {
    this.httpClient.post<{ read: number }>(this.host + '/conversations/' + userId + '/read', {}).subscribe({
      error: err => console.error('Could not mark conversation as read:', err),
    });
  }

  sendTyping(to: string, typing: boolean): void {
    this.socket?.emit('typing', { to, typing });
  }

  // Signalisation des appels : avec accusé de réception du serveur (null si pas de réponse ou socket fermé)
  emitCall<T = { ok: boolean; reason?: string }>(event: string, payload: unknown, withAck = false): Promise<T | null> {
    const socket = this.socket;
    if (!socket?.connected) {
      return Promise.resolve(null);
    }
    if (!withAck) {
      socket.emit(event, payload);
      return Promise.resolve(null);
    }
    return socket.timeout(CALL_ACK_TIMEOUT_MS).emitWithAck(event, payload).catch(() => null);
  }

  private connect(token: string): void {
    this.disconnect();
    // environment.host = origine de l'API, éventuellement suivie d'un préfixe (ex. https://promodeep.com/api)
    const url = new URL(environment.host, window.location.href);
    const socket = io(url.origin, {
      path: url.pathname.replace(/\/$/, '') + '/socket.io',
      // En fonction : la visibilité est relue à chaque reconnexion
      auth: callback => callback({ token, visible: document.visibilityState === 'visible' }),
    });
    this.socket = socket;

    // Aussi à chaque reconnexion : des événements ont pu être manqués
    socket.on('connect', () => this.refresh());
    socket.on('message', (message: ChatMessage) => this.onMessage(message));
    socket.on('read', (event: ChatReadEvent) => this.onRead(event));
    socket.on('typing', (event: ChatTypingEvent) => this.typing$.next(event));
    socket.on('presence', (event: { userId: string; online: boolean }) => this.onPresence(event));
    socket.on('relations', (event: ChatRelationEvent) => {
      this.refresh();
      this.relationEvents$.next(event);
    });
    for (const type of ['incoming', 'accepted', 'rejected', 'signal', 'ended', 'handled'] as const) {
      socket.on('call:' + type, (payload: object) => this.callEvents$.next({ ...payload, type } as CallSocketEvent));
    }
    // Le serveur raccroche les appels de ce socket à sa déconnexion
    socket.on('disconnect', () => this.callEvents$.next({ type: 'disconnected' }));
  }

  // Le serveur prévient par notification un membre dont aucune page n'est au premier plan.
  // Retour au premier plan : le socket coupé par le téléphone se reconnecte sans attendre son délai.
  private onVisibilityChange(): void {
    const visible = document.visibilityState === 'visible';
    if (this.socket?.connected) {
      this.socket.emit('visibility', { visible });
    } else if (visible) {
      this.socket?.connect();
    }
  }

  private disconnect(): void {
    if (this.socket) {
      this.callEvents$.next({ type: 'disconnected' });
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.relations.next(null);
    this.unread.next(EMPTY_UNREAD);
    this.activeConversation = null;
  }

  private refresh(): void {
    this.loadRelations().subscribe({ error: err => console.error('Could not load chat relations:', err) });
    this.loadUnread().subscribe({ error: err => console.error('Could not load chat unread count:', err) });
  }

  private onMessage(message: ChatMessage): void {
    const me = this.myId;
    const incoming = String(message.to) === String(me);
    const friendId = incoming ? String(message.from) : String(message.to);
    const counted = incoming && friendId !== this.activeConversation;

    const relations = this.relations.value;
    if (relations) {
      const friends = relations.friends.map(friend => String(friend.user._id) !== friendId ? friend : {
        ...friend,
        lastMessage: { text: message.text, from: message.from, createdAt: message.createdAt },
        unread: counted ? friend.unread + 1 : friend.unread,
      });
      // La conversation la plus récente remonte en tête de liste
      friends.sort((a, b) => this.activity(b) - this.activity(a));
      this.relations.next({ ...relations, friends });
    }
    if (counted) {
      const unread = this.unread.value;
      this.unread.next({ ...unread, messages: unread.messages + 1 });
    }
    this.messages$.next(message);
  }

  private onRead(event: ChatReadEvent): void {
    if (String(event.reader) === String(this.myId)) {
      // Lu ici ou depuis un autre appareil : le badge et la liste se recalent sur le serveur
      const relations = this.relations.value;
      if (relations) {
        const friends = relations.friends.map(friend =>
          this.conversationKey(friend.user._id) === event.conversationKey ? { ...friend, unread: 0 } : friend);
        this.relations.next({ ...relations, friends });
      }
      this.loadUnread().subscribe({ error: () => { } });
    }
    this.read$.next(event);
  }

  private onPresence(event: { userId: string; online: boolean }): void {
    const relations = this.relations.value;
    if (!relations) {
      return;
    }
    const friends = relations.friends.map(friend =>
      String(friend.user._id) === String(event.userId) ? { ...friend, online: event.online } : friend);
    this.relations.next({ ...relations, friends });
  }

  conversationKey(friendId: string): string {
    return [String(this.myId), String(friendId)].sort().join('_');
  }

  private activity(friend: { since: string; lastMessage: { createdAt: string } | null }): number {
    return new Date(friend.lastMessage ? friend.lastMessage.createdAt : friend.since).getTime() || 0;
  }

  private storeRelations(request: Observable<ChatRelations>): Observable<ChatRelations> {
    return request.pipe(tap(relations => {
      this.relations.next(relations);
      this.unread.next({ ...this.unread.value, requests: relations.incoming.length });
    }));
  }
}
